import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import i18next from 'i18next';
import axios from 'axios';
import './HolidayMode.css'; // Import the CSS file
import WithLoading from '../WithLoading/WithLoading';
import ShowError from '../ShowError/ShowError';

function HolidayMode({t}) {
  const [holiday, setHoliday] = useState({ startDate: null, endDate: null });
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");

  const DisplayCurrentHolidayWithLoading = WithLoading(DisplayCurrentHoliday);

  useEffect(() => {
    const fetchHoliday = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL + `/qr/info/holiday/STI%23` + encodeURIComponent(id) + '?time=' + Date.now(), {timeout: 30000});
        setHoliday({
            startDate: new Date(response.data.startDate),
            endDate: new Date(response.data.endDate)
        });
      } catch (error) {
        console.error('Error fetching holiday data:', error);
        setErrorMessage(t("error.loading"));
      } finally {
        setLoading(false);
      }
    };

    fetchHoliday();
  }, [id, t]);

  const cancelHoliday = async () => {
    try {
      setLoading(true);
      const response = await axios.post(process.env.REACT_APP_API_URL + `/qr/info/holiday/STI%23` + encodeURIComponent(id), {
        startDate: null,
        endDate: null,
      }, {timeout: 30000});
      setHoliday({
        startDate: new Date(response.data.startDate),
        endDate: new Date(response.data.endDate)
      });
    } catch (error) {
      console.error('Error cancelling holiday:', error);
      setErrorMessage(t("error.updating"));
    } finally {
      setLoading(false);
    }
  };

  if (errorMessage) {
    return <ShowError error_msg={errorMessage} />;
  }

  return (
    <div className="holiday-mode-container">
      <h2>{t("holiday")}</h2>
      <p>{t("holiday.explanation")}</p>
      <div align={loading ? "center" : "left"}>
        <DisplayCurrentHolidayWithLoading isLoading={loading} t={t} holiday={holiday} setShowPopup={setShowPopup} cancelHoliday={cancelHoliday} />
      </div>
      {showPopup && <UpdateHolidayPopup t={t} setShowPopup={setShowPopup} setHoliday={setHoliday} setLoading={setLoading} setErrorMessage={setErrorMessage} id={id} />}
    </div>
  );
}

function formatDate(date) {
  return date.toLocaleDateString(i18next.language, { year: 'numeric', month: 'long', day: 'numeric' });
}

function DisplayCurrentHoliday({t, holiday, setShowPopup, cancelHoliday}) {
    const now = new Date();
    const futureHolidaySet = holiday.startDate !== null && holiday.startDate > now;
    const activeHoliday = holiday.startDate !== null && holiday.startDate <= now && holiday.endDate >= now;
    const returnDate = new Date(holiday.endDate);
    returnDate.setDate(returnDate.getDate() + 1);

    return (
    <>
        {futureHolidaySet && <div className="holiday-dates future-holiday">
            <p><b>{t("holiday.start_date")}:</b> <br /> {formatDate(holiday.startDate)}</p>
            <p><b>{t("holiday.end_date")}:</b> <br />{formatDate(holiday.endDate)}</p>
            <p className="warning-message">{t("holiday.hot_water_return")}: <br />{formatDate(returnDate)}</p>
        </div>}
        {activeHoliday && <div className="holiday-dates active-holiday">
            <p><b>{t("holiday.currently_active")}</b></p>
            <p><b>{t("holiday.end_date")}:</b> <br /> {formatDate(holiday.endDate)}</p>
            <p className="warning-message">{t("holiday.hot_water_return")}: <br />{formatDate(returnDate)}</p>
        </div>}
        <div className="button-container">
          {!activeHoliday && <button onClick={() => setShowPopup(true)}>{futureHolidaySet ? t("holiday.change") : t("holiday.add")}</button>}
          {(futureHolidaySet || activeHoliday) && <button onClick={cancelHoliday} className="cancel-holiday-button">
            {t("holiday.cancel")}
          </button>}
        </div>
    </>
    );
}

function UpdateHolidayPopup({t, setShowPopup, setHoliday, setLoading, setErrorMessage, id}) {
    const [newStartDate, setNewStartDate] = useState('');
    const [newEndDate, setNewEndDate] = useState('');
    const [localErrorMessage, setLocalErrorMessage] = useState('');

    const handleSave = async () => {
        const startDate = new Date(newStartDate);
        const endDate = new Date(newEndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (startDate < today || endDate < today) {
            setLocalErrorMessage(t("holiday.popup.past_date_msg"));
            return;
        }

        if (endDate <= startDate) {
            setLocalErrorMessage(t("holiday.popup.end_before_start_msg"));
            return;
        }
        setShowPopup(false);
        setLoading(true);
        try {
          const response = await axios.post(process.env.REACT_APP_API_URL + `/qr/info/holiday/STI%23` + encodeURIComponent(id), {
            startDate: newStartDate,
            endDate: newEndDate,
          }, {timeout: 30000});
          setHoliday({
            startDate: new Date(response.data.startDate),
            endDate: new Date(response.data.endDate)
          });
        } catch (error) {
          console.error('Error saving holiday data:', error);
          setErrorMessage(t("error.updating"));
        }
        finally {
          setLoading(false);
        }
      };

    return (
        <div className="popup">
          <div className="popup-content-holiday">
            <h3>{t("holiday.popup.title")}</h3>
            <label>
              {t("holiday.popup.start")}:
              <input type="date" value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)} />
            </label>
            <label>
              {t("holiday.popup.end")}:
              <input type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
            </label>
            {localErrorMessage && <p className="error-message">{localErrorMessage}</p>}
            <div className="button-container">
              <button onClick={handleSave} disabled={!newStartDate || !newEndDate}>{t("button.submit")}</button>
              <button onClick={() => setShowPopup(false)} className="cancel-button">{t("button.cancel")}</button>
            </div>
          </div>
        </div>
    );
}

export default withTranslation()(HolidayMode);
